import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Crown, Loader2, Puzzle, Check, Trophy, ArrowLeftRight, Plus, ChevronRight, Clock3, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  sent: "bg-[#E6ECF5] text-[#1B4383]",
  quoted: "bg-[#E4EEE8] text-[#527D63]",
  costing_done: "bg-[#DCE8F5] text-[#1B4383] font-semibold",
  awarded: "bg-[#F6EDD9] text-[#A87F31]",
  declined: "bg-slate-100 text-slate-400 line-through",
};
const STATUS_LABEL = { sent: "Sent RFQ", quoted: "Quote Received", costing_done: "Costing Done", awarded: "Awarded", declined: "RFQ Declined" };
const prettyStatus = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const tzs0 = (v) => (v == null ? "—" : `TZS ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtNum = (v) => (v == null || v === "" ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

export default function ComparePanel({ rfqId, onChanged }) {
  const [items, setItems] = useState([]);
  const [extras, setExtras] = useState([]);
  const [landed, setLanded] = useState({ ranked: [], pending: [], per_item: {} });
  const [busy, setBusy] = useState("");
  const [openItems, setOpenItems] = useState({});
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    const [it, ex, ld] = await Promise.all([
      api.get(`/rfqs/${rfqId}/items`),
      api.get(`/rfqs/${rfqId}/compare-extras`).catch(() => ({ data: [] })),
      api.get(`/rfqs/${rfqId}/landed-compare`).catch(() => ({ data: { ranked: [], pending: [] } })),
    ]);
    setItems(it.data);
    setExtras(ex.data);
    setLanded(ld.data || { ranked: [], pending: [], per_item: {} });
  }, [rfqId]);
  useEffect(() => { load(); }, [load]);

  const award = async (link) => {
    setBusy(link.id);
    try { await api.post(`/item-suppliers/${link.id}/award`); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const toggleExtra = async (ex) => {
    setBusy(ex.id);
    try { await api.post(`/quote-extras/${ex.id}/toggle-award`); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const toggleOpen = (id) => setOpenItems((o) => ({ ...o, [id]: !o[id] }));

  const withSuppliers = items.filter((i) => (i.suppliers || []).length > 0);
  const fmt = (l) => (l.quoted_price != null ? `${l.currency ? l.currency + " " : ""}${fmtNum(l.quoted_price)}` : "—");
  const fmtExtra = (e) => (e.unit_price != null ? fmtNum(e.unit_price) : "—");
  const extrasFor = (itemId) => extras.filter((e) => e.parent_item_id === itemId);
  const standalone = extras.filter((e) => e.kind === "addon" && !e.parent_item_id);

  const leadBySupplier = {};
  items.forEach((it) => (it.suppliers || []).forEach((s) => {
    if (s.lead_time && !leadBySupplier[s.supplier_id]) leadBySupplier[s.supplier_id] = s.lead_time;
  }));
  // Ex Works Dar lead time (supplier + transit + COC) per supplier, from the costing.
  const leadDarBySupplier = {};
  [...(landed.ranked || []), ...(landed.pending || [])].forEach((r) => {
    if (r.lead) leadDarBySupplier[r.supplier_id] = r.lead;
  });

  const ExtraRow = ({ e, landedCol }) => (
    <tr className={`border-t border-border ${e.awarded ? "bg-[#E4EEE8]" : "bg-[#FBFCFE]"}`} data-testid={`compare-extra-${e.id}`}>
      <td className="px-3 py-2 text-slate-700">
        <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-[#EEF0F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          <Puzzle className="h-3 w-3" />{e.kind === "alternative" ? "Alt" : e.optional ? "Add-on · opt" : "Add-on"}
        </span>
        {e.description || "(unnamed)"}
        <span className="block text-[11px] text-muted-foreground">{e.supplier_name}</span>
        {e.kind === "alternative" && e.awarded && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#EAF1FB] px-2 py-0.5 text-[10px] font-medium text-[#1B4383]" data-testid={`alt-replaces-${e.id}`}>
            <ArrowLeftRight className="h-3 w-3" /> Replaces the base line — base excluded from totals
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-center text-xs text-muted-foreground">{e.quantity != null ? `Qty ${e.quantity}` : "—"}</td>
      <td className="px-3 py-2 text-right font-mono">{fmtExtra(e)}</td>
      {landedCol && <td className="px-3 py-2 text-right text-muted-foreground">—</td>}
      <td className="px-3 py-2 text-muted-foreground">{e.lead_time || "—"}</td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant={e.awarded ? "default" : "outline"} onClick={() => toggleExtra(e)} disabled={busy === e.id} data-testid={`include-extra-${e.id}`}>
          {busy === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : e.awarded ? <Check className="mr-1 h-4 w-4" /> : null}
          {e.awarded ? "Included" : "Include"}
        </Button>
      </td>
    </tr>
  );

  const anyHidden = withSuppliers.some((i) => (i.suppliers || []).some((s) => s.quoted_price == null && !s.awarded));

  const ExtraCard = ({ e }) => (
    <div className={`rounded-lg border p-3 ${e.awarded ? "border-[#527D63] bg-[#E4EEE8]" : "border-border bg-[#FBFCFE]"}`} data-testid={`compare-extra-m-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF0F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500"><Puzzle className="h-3 w-3" />{e.kind === "alternative" ? "Alt" : e.optional ? "Add-on · opt" : "Add-on"}</span>
            <span className="break-words font-medium text-slate-800">{e.description || "(unnamed)"}</span>
          </div>
          <div className="text-[11px] text-muted-foreground">{e.supplier_name}</div>
        </div>
        <Button size="sm" variant={e.awarded ? "default" : "outline"} onClick={() => toggleExtra(e)} disabled={busy === e.id} data-testid={`include-extra-m-${e.id}`}>
          {busy === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : e.awarded ? <Check className="mr-1 h-4 w-4" /> : null}{e.awarded ? "Included" : "Include"}
        </Button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2 text-xs">
        <div><div className="text-[11px] text-muted-foreground">Qty</div><div className="text-slate-700">{e.quantity != null ? e.quantity : "—"}</div></div>
        <div><div className="text-[11px] text-muted-foreground">Buying Price</div><div className="font-mono text-slate-800">{fmtExtra(e)}</div></div>
        <div className="col-span-2"><div className="text-[11px] text-muted-foreground">Lead Time</div><div className="text-slate-700">{e.lead_time || "—"}</div></div>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-border bg-white">
      <div className="border-b border-border px-5 py-4">
        <h2 className="font-heading text-base font-bold tracking-tight">Compare Quotes</h2>
        <p className="text-sm text-muted-foreground">Pick the winning supplier by landed cost, then add them to the Somcore Quote along with any add-ons.</p>
      </div>

      {/* ── Step 1: the decision — landed-cost ranking cards ── */}
      {(landed.ranked.length > 0 || landed.pending.length > 0) && (
        <div className="border-b border-border bg-[#FAFBFD] px-5 py-4" data-testid="landed-compare">
          <div className="mb-1 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#A87F31]" />
            <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">Landed-Cost Ranking (TZS)</h3>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">Total delivered selling cost per supplier from each <b className="font-semibold text-slate-600">Confirmed Quote Basis</b> (incl. freight, duty &amp; margin). Lowest wins. <span className="text-[#A87F31]">Provisional</span> = costed but not yet confirmed — open the costing and click “Confirm as Quote Basis” to lock it.</p>
          {landed.ranked.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {landed.ranked.map((r) => (
                <div
                  key={r.supplier_id}
                  data-testid={`landed-card-${r.supplier_id}`}
                  className={`relative rounded-xl border p-4 transition-shadow hover:shadow-sm ${r.is_cheapest ? "border-[#527D63] bg-[#F3F8F4]" : "border-border bg-white"}`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${r.is_cheapest ? "bg-[#527D63] text-white" : "bg-slate-100 text-slate-500"}`}>{r.rank}</span>
                    {r.method && <span className="rounded-full bg-[#EEF0F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 capitalize">{r.method}</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {r.is_cheapest && <Crown className="h-4 w-4 shrink-0 text-[#527D63]" />}
                    <span className="truncate font-semibold text-slate-900" title={r.supplier_name}>{r.supplier_name}</span>
                  </div>
                  <div className="mt-1 font-mono text-lg font-bold tracking-tight text-slate-900">{tzs0(r.selling_tzs)}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                    {r.confirmed
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 font-medium text-[#527D63]" data-testid={`landed-confirmed-${r.supplier_id}`}><Check className="h-3 w-3" />Confirmed</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-[#FBF0DE] px-1.5 py-0.5 font-medium text-[#A87F31]" data-testid={`landed-provisional-${r.supplier_id}`}>Provisional</span>}
                    {(r.lead || leadBySupplier[r.supplier_id]) && <span className="inline-flex items-center gap-1"><Clock3 className="h-3 w-3" />{r.lead || leadBySupplier[r.supplier_id]}</span>}
                    {r.priced_count != null && r.item_count != null && <span>{r.priced_count}/{r.item_count} lines priced</span>}
                  </div>
                  {r.is_cheapest && <span className="absolute right-3 top-3 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">cheapest</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No costings saved yet — open a supplier quote and save its costing to rank landed cost here.</p>
          )}
          {landed.pending.length > 0 && (
            <p className="mt-3 text-[11px] text-muted-foreground" data-testid="landed-pending">
              Not yet costed: {landed.pending.map((p) => p.supplier_name).join(", ")} — save a costing on each to include them.
            </p>
          )}
        </div>
      )}

      {!withSuppliers.length && !extras.length && <div className="px-5 py-10 text-center text-muted-foreground">No suppliers attached to any item yet.</div>}

      {/* ── Step 2: item-by-item detail (collapsed by default) ── */}
      {withSuppliers.length > 0 && (
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">Item-by-Item Comparison</h3>
          {anyHidden || showAll ? (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
              data-testid="compare-show-all-toggle"
            >
              {showAll ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showAll ? "Hide Non-Quoted Suppliers" : "Show All Invited Suppliers"}
            </button>
          ) : null}
        </div>
      )}

      <div className="divide-y divide-border">
        {withSuppliers.map((item, idx) => {
          const priced = (item.suppliers || []).filter((s) => s.quoted_price != null);
          const currencies = new Set(priced.map((s) => s.currency || ""));
          const lowest = priced.length && currencies.size === 1 ? Math.min(...priced.map((s) => s.quoted_price)) : null;
          const itemExtras = extrasFor(item.id);
          const hasAwardedAlt = itemExtras.some((e) => e.kind === "alternative" && e.awarded);
          const landedMap = (landed.per_item && landed.per_item[item.id]) || {};
          const landedVals = Object.values(landedMap).filter((v) => v != null);
          const lowestLanded = landedVals.length ? Math.min(...landedVals) : null;
          const awardedSup = (item.suppliers || []).find((s) => s.awarded);
          const visible = showAll ? item.suppliers : item.suppliers.filter((s) => s.quoted_price != null || s.awarded);
          const open = !!openItems[item.id];
          return (
            <div key={item.id} data-testid={`compare-item-${item.id}`}>
              {/* collapsed summary row */}
              <button
                type="button"
                onClick={() => toggleOpen(item.id)}
                className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50"
                data-testid={`compare-item-toggle-${item.id}`}
              >
                <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                <span className="font-mono text-xs text-muted-foreground">#{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-900">{item.description || "(untitled)"}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.spec_or_part ? `${item.spec_or_part} · ` : ""}Qty {item.quantity ?? "—"}{item.unit ? ` ${item.unit}` : ""}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] sm:hidden">
                    <span className="text-muted-foreground">{priced.length} Quoted</span>
                    {lowestLanded != null && <span className="font-mono text-slate-700">Best <b className="text-[#527D63]">{tzs0(lowestLanded)}</b></span>}
                    {awardedSup ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 font-medium text-[#527D63]"><Crown className="h-3 w-3" />{awardedSup.supplier_name}</span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-medium text-slate-400">None Picked</span>
                    )}
                  </div>
                </div>
                <div className="hidden items-center gap-4 text-xs sm:flex">
                  <span className="text-muted-foreground">{priced.length} Quoted</span>
                  {lowestLanded != null && (
                    <span className="font-mono text-slate-700">Best <b className="text-[#527D63]">{tzs0(lowestLanded)}</b></span>
                  )}
                  {awardedSup ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#E4EEE8] px-2 py-0.5 font-medium text-[#527D63]" data-testid={`compare-awarded-${item.id}`}>
                      <Crown className="h-3 w-3" />{awardedSup.supplier_name}
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-400">None Picked</span>
                  )}
                </div>
              </button>

              {/* expanded detail */}
              {open && (
                <div className="px-5 pb-5">
                  {visible.length === 0 ? (
                    <p className="rounded-md border border-dashed border-border bg-[#FBFCFE] px-3 py-4 text-center text-xs text-muted-foreground">
                      No quotes recorded yet for this line. Use “Show All Invited Suppliers” to see who was sent the RFQ.
                    </p>
                  ) : (
                    <>
                    <div className="hidden overflow-x-auto sm:block">
                      <table className="w-full table-fixed text-sm border border-border rounded">
                        <colgroup>
                          <col className="w-[26%]" /><col className="w-[13%]" /><col className="w-[19%]" />
                          <col className="w-[16%]" /><col className="w-[12%]" /><col className="w-[14%]" />
                        </colgroup>
                        <thead>
                          <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                            <th className="px-3 py-2 font-medium">Supplier</th>
                            <th className="px-3 py-2 font-medium text-center">Status</th>
                            <th className="px-3 py-2 font-medium text-right">Buying Price (Native Currency)</th>
                            <th className="px-3 py-2 font-medium text-right">Landed/Unit (TZS)</th>
                            <th className="px-3 py-2 font-medium">Lead Time</th>
                            <th className="px-3 py-2 font-medium text-right">Somcore Quote</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visible.map((l) => (
                            <tr key={l.id} className={`border-t border-border ${l.awarded ? (hasAwardedAlt ? "bg-slate-50" : "bg-[#E4EEE8]") : ""}`} data-testid={`compare-row-${l.id}`}>
                              <td className="px-3 py-2 font-medium text-slate-900">
                                <span className="inline-flex items-center gap-1">
                                  {l.awarded && <Crown className={`h-3.5 w-3.5 ${hasAwardedAlt ? "text-slate-400" : "text-[#527D63]"}`} />}
                                  <span className={l.awarded && hasAwardedAlt ? "text-slate-400 line-through" : ""}>{l.supplier_name}</span>
                                </span>
                                {l.awarded && hasAwardedAlt && <span className="ml-2 rounded-full bg-[#EAF1FB] px-1.5 py-0.5 text-[10px] font-medium text-[#1B4383]">replaced by alternative</span>}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[l.status] || "bg-slate-100 text-slate-500"}`}>{STATUS_LABEL[l.status] || prettyStatus(l.status)}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {fmt(l)}
                                {l.quoted_price != null && l.quoted_price === lowest && priced.length > 1 && (
                                  <span className="ml-2 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">lowest</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {landedMap[l.supplier_id] == null ? <span className="text-muted-foreground">—</span> : tzs0(landedMap[l.supplier_id])}
                                {landedMap[l.supplier_id] != null && landedMap[l.supplier_id] === lowestLanded && landedVals.length > 1 && (
                                  <span className="ml-2 rounded-full bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">Lowest Landed</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{leadDarBySupplier[l.supplier_id] || l.lead_time || "—"}</td>
                              <td className="px-3 py-2 text-right">
                                <Button size="sm" variant={l.awarded ? "default" : "outline"} onClick={() => award(l)} disabled={busy === l.id} data-testid={`award-${l.id}`}>
                                  {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : l.awarded ? <Check className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}
                                  {l.awarded ? "Added" : "Add to Quote"}
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {itemExtras.map((e) => <ExtraRow key={e.id} e={e} landedCol />)}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="space-y-2 sm:hidden">
                      {visible.map((l) => (
                        <div key={l.id} className={`rounded-lg border p-3 ${l.awarded ? (hasAwardedAlt ? "border-slate-200 bg-slate-50" : "border-[#527D63] bg-[#E4EEE8]") : "border-border bg-white"}`} data-testid={`compare-row-m-${l.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1 font-medium text-slate-900">
                                {l.awarded && <Crown className={`h-3.5 w-3.5 shrink-0 ${hasAwardedAlt ? "text-slate-400" : "text-[#527D63]"}`} />}
                                <span className={`break-words ${l.awarded && hasAwardedAlt ? "text-slate-400 line-through" : ""}`}>{l.supplier_name}</span>
                              </div>
                              <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[l.status] || "bg-slate-100 text-slate-500"}`}>{STATUS_LABEL[l.status] || prettyStatus(l.status)}</span>
                            </div>
                            <Button size="sm" variant={l.awarded ? "default" : "outline"} onClick={() => award(l)} disabled={busy === l.id} data-testid={`award-m-${l.id}`}>
                              {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : l.awarded ? <Check className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}
                              {l.awarded ? "Added" : "Add"}
                            </Button>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2 text-xs">
                            <div>
                              <div className="text-[11px] text-muted-foreground">Buying Price</div>
                              <div className="font-mono text-slate-800">{fmt(l)}{l.quoted_price != null && l.quoted_price === lowest && priced.length > 1 ? <span className="ml-1 text-[10px] text-[#527D63]">lowest</span> : null}</div>
                            </div>
                            <div>
                              <div className="text-[11px] text-muted-foreground">Landed/Unit (TZS)</div>
                              <div className="font-mono text-slate-800">{landedMap[l.supplier_id] == null ? "—" : tzs0(landedMap[l.supplier_id])}</div>
                            </div>
                            <div className="col-span-2">
                              <div className="text-[11px] text-muted-foreground">Lead Time</div>
                              <div className="text-slate-700">{leadDarBySupplier[l.supplier_id] || l.lead_time || "—"}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {itemExtras.map((e) => <ExtraCard key={e.id} e={e} />)}
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {standalone.length > 0 && (
        <div className="border-t border-border p-5" data-testid="compare-standalone">
          <div className="mb-3 font-medium text-slate-900">Standalone add-ons <span className="text-xs font-normal text-muted-foreground">(freight, commissioning, etc.)</span></div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm border border-border rounded">
              <thead>
                <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 font-medium text-center">Qty</th>
                  <th className="px-3 py-2 font-medium text-right">Buying Price (Native Currency)</th>
                  <th className="px-3 py-2 font-medium">Lead Time</th>
                  <th className="px-3 py-2 font-medium text-right">Include</th>
                </tr>
              </thead>
              <tbody>{standalone.map((e) => <ExtraRow key={e.id} e={e} />)}</tbody>
            </table>
          </div>
          <div className="space-y-2 sm:hidden">{standalone.map((e) => <ExtraCard key={e.id} e={e} />)}</div>
        </div>
      )}
    </div>
  );
}
